// THE QUOTES LIST VIEW (quotes-list-usability, 2026-10-06) -- the PURE logic.
//
// The view is everything the Quotes screen asks the server for: page, sort,
// search, filters. It lives in the URL (so a link or a reload comes back to the
// same view) and is sent to /anixify/api/quote_list, where SCOPE is decided --
// nothing here can widen what a role may see; these are only requests to
// narrow it. The page SIZE is a per-browser preference, not part of the URL.

export type SortKey = "name" | "date" | "customer" | "owner" | "status" | "total";
export type SortDir = "asc" | "desc";
export type StatusKey = "draft" | "awaiting" | "open" | "expired" | "won" | "lost" | "superseded";

export const SORT_KEYS: readonly SortKey[] = ["name", "date", "customer", "owner", "status", "total"];
export const STATUS_KEYS: readonly StatusKey[] = ["draft", "awaiting", "open", "expired", "won", "lost", "superseded"];
export const STATUS_LABELS: Record<StatusKey, string> = {
  draft: "Draft", awaiting: "Awaiting approval", open: "Open", expired: "Expired",
  won: "Won", lost: "Lost", superseded: "Superseded",
};

export const QUOTES_PAGE_SIZES = [25, 50, 100] as const;
export type QuotesPageSize = (typeof QUOTES_PAGE_SIZES)[number];
export const DEFAULT_QUOTES_PAGE_SIZE: QuotesPageSize = 50;
export const QUOTES_PAGE_SIZE_STORAGE_KEY = "anixify.quotesPageSize";

export interface QuotesView {
  page: number;              // 1-based
  sort: SortKey;
  dir: SortDir;
  q: string;
  status: StatusKey | "";
  customer: number | null;
  owner: number | null;
  from: string;              // YYYY-MM-DD or ""
  to: string;
  followUp: boolean;
}

/** Follow up on, no column chosen: the server orders it by Valid Until. */
export function isDueOrder(view: QuotesView): boolean {
  return view.followUp && view.sort === DEFAULT_VIEW.sort && view.dir === DEFAULT_VIEW.dir;
}

/** Newest first. */
export const DEFAULT_VIEW: QuotesView = {
  page: 1, sort: "date", dir: "desc", q: "", status: "", customer: null, owner: null,
  from: "", to: "", followUp: false,
};

const isDay = (v: string) => /^\d{4}-\d{2}-\d{2}$/.test(v) && !Number.isNaN(Date.parse(v));
const posInt = (v: string | null): number | null => {
  if (v === null || !/^\d+$/.test(v)) return null;
  const n = Number(v);
  return n > 0 ? n : null;
};

/** The view a URL describes. Tolerant: anything unusable falls back to the
 *  default for that field, so a stale or hand-edited link still opens. */
export function parseView(search: string): QuotesView {
  const p = new URLSearchParams(search);
  const sort = p.get("sort") as SortKey | null;
  const status = p.get("status") as StatusKey | null;
  const from = p.get("from") ?? "";
  const to = p.get("to") ?? "";
  return {
    page: posInt(p.get("page")) ?? 1,
    sort: sort && SORT_KEYS.includes(sort) ? sort : DEFAULT_VIEW.sort,
    dir: p.get("dir") === "asc" ? "asc" : p.get("dir") === "desc" ? "desc"
      : sort && SORT_KEYS.includes(sort) ? defaultDir(sort) : DEFAULT_VIEW.dir,
    q: (p.get("q") ?? "").trim().slice(0, 80),
    status: status && STATUS_KEYS.includes(status) ? status : "",
    customer: posInt(p.get("customer")),
    owner: posInt(p.get("owner")),
    from: isDay(from) ? from : "",
    to: isDay(to) ? to : "",
    followUp: p.get("follow_up") === "1",
  };
}

/** The query string for a view, omitting every default so the plain list has a
 *  plain URL. */
export function viewToSearch(view: QuotesView): string {
  const p = new URLSearchParams();
  if (view.q) p.set("q", view.q);
  if (view.status) p.set("status", view.status);
  if (view.customer !== null) p.set("customer", String(view.customer));
  if (view.owner !== null) p.set("owner", String(view.owner));
  if (view.from) p.set("from", view.from);
  if (view.to) p.set("to", view.to);
  if (view.followUp) p.set("follow_up", "1");
  if (view.sort !== DEFAULT_VIEW.sort || view.dir !== DEFAULT_VIEW.dir) {
    p.set("sort", view.sort);
    p.set("dir", view.dir);
  }
  if (view.page > 1) p.set("page", String(view.page));
  const s = p.toString();
  return s ? `?${s}` : "";
}

/** The first click on a column: text columns A-Z, date and total newest/biggest first. */
export function defaultDir(sort: SortKey): SortDir {
  return sort === "date" || sort === "total" ? "desc" : "asc";
}

/** A header click: the same column flips its direction, a new one starts at its
 *  natural direction. Back to page 1 either way. */
export function toggleSort(view: QuotesView, sort: SortKey): QuotesView {
  if (view.sort === sort) return { ...view, dir: view.dir === "asc" ? "desc" : "asc", page: 1 };
  return { ...view, sort, dir: defaultDir(sort), page: 1 };
}

/** A filter changed: the page restarts. */
export function withFilter(view: QuotesView, change: Partial<QuotesView>): QuotesView {
  return { ...view, ...change, page: 1 };
}

export type ChipKey = "q" | "status" | "customer" | "owner" | "from" | "to" | "followUp";
export interface Chip { key: ChipKey; label: string }

/** What is narrowing the list, as removable chips. */
export function activeChips(
  view: QuotesView,
  lookup: { customer?: (id: number) => string | undefined; owner?: (id: number) => string | undefined } = {},
): Chip[] {
  const out: Chip[] = [];
  if (view.q) out.push({ key: "q", label: `Search: “${view.q}”` });
  if (view.status) out.push({ key: "status", label: `Status: ${STATUS_LABELS[view.status]}` });
  if (view.customer !== null) {
    out.push({ key: "customer", label: `Customer: ${lookup.customer?.(view.customer) ?? `#${view.customer}`}` });
  }
  if (view.owner !== null) {
    out.push({ key: "owner", label: `Owner: ${lookup.owner?.(view.owner) ?? `#${view.owner}`}` });
  }
  if (view.from) out.push({ key: "from", label: `From ${view.from}` });
  if (view.to) out.push({ key: "to", label: `To ${view.to}` });
  if (view.followUp) out.push({ key: "followUp", label: "Follow up" });
  return out;
}

const CLEARED: Record<ChipKey, Partial<QuotesView>> = {
  q: { q: "" }, status: { status: "" }, customer: { customer: null }, owner: { owner: null },
  from: { from: "" }, to: { to: "" }, followUp: { followUp: false },
};

export function removeChip(view: QuotesView, key: ChipKey): QuotesView {
  return withFilter(view, CLEARED[key]);
}

/** Every filter off. The sort and the page size are not filters and stay. */
export function clearAll(view: QuotesView): QuotesView {
  return { ...view, q: "", status: "", customer: null, owner: null, from: "", to: "", followUp: false, page: 1 };
}

export function isFiltered(view: QuotesView): boolean {
  return activeChips(view).length > 0;
}

/** The request for /anixify/api/quote_list. */
export function toApiParams(view: QuotesView, pageSize: QuotesPageSize): Record<string, string> {
  const out: Record<string, string> = { page: String(view.page), page_size: String(pageSize) };
  // The follow-up list keeps its own order (soonest due first) until a column is chosen.
  if (!isDueOrder(view)) { out.sort = view.sort; out.dir = view.dir; }
  if (view.q) out.q = view.q;
  if (view.status) out.status = view.status;
  if (view.customer !== null) out.customer = String(view.customer);
  if (view.owner !== null) out.owner = String(view.owner);
  if (view.from) out.date_from = view.from;
  if (view.to) out.date_to = view.to;
  if (view.followUp) out.follow_up = "1";
  return out;
}

// ---- the remembered page size (per browser) ---------------------------------------

export function isQuotesPageSize(v: unknown): v is QuotesPageSize {
  return QUOTES_PAGE_SIZES.some((s) => s === v);
}

export function readQuotesPageSize(): QuotesPageSize {
  try {
    const raw = window.localStorage.getItem(QUOTES_PAGE_SIZE_STORAGE_KEY);
    const n = raw === null ? NaN : Number(raw);
    return isQuotesPageSize(n) ? n : DEFAULT_QUOTES_PAGE_SIZE;
  } catch {
    return DEFAULT_QUOTES_PAGE_SIZE;
  }
}

export function storeQuotesPageSize(size: QuotesPageSize): void {
  try {
    window.localStorage.setItem(QUOTES_PAGE_SIZE_STORAGE_KEY, String(size));
  } catch {
    /* blocked storage: the choice just is not remembered */
  }
}
